/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useCallback, useEffect } from 'react';
import { backendPropertiesCrud } from '@services/housien';
import {
  buildPropertySublabel,
  formatPropertyLabel,
} from '@/shared/utils/property-label';

export interface PropertyOption {
  id: string;
  label: string;
  sublabel: string;
  raw: any;
}

/**
 * Maps raw property data into a display-friendly option format.
 */
function mapPropertyToOption(p: any): PropertyOption {
  return {
    id: p.id,
    // The headline-first rule (and its "undefined" guard) now lives in
    // formatPropertyLabel, so the picker and the leases list cannot drift.
    label: formatPropertyLabel(p),
    sublabel: buildPropertySublabel(p),
    raw: p,
  };
}

/**
 * Loads and caches the agency's properties as picker options.
 *
 * @param agencyId - The agency whose properties to load; when absent the hook idles.
 * @returns The property options, a loading flag, and a reload function.
 */
export function useAgencyProperties(agencyId?: string) {
  const [properties, setProperties] = useState<PropertyOption[]>([]);
  const [isLoading, setIsLoading] = useState(Boolean(agencyId));

  const load = useCallback(async () => {
    if (!agencyId) return;
    setIsLoading(true);
    try {
      const response = await backendPropertiesCrud(agencyId).search({
        pagination: { page: '1', limit: '50' },
      });
      const items = Object.values(response.data ?? {}) as any[];
      setProperties(items.map(mapPropertyToOption));
    } catch {
      setProperties([]);
    } finally {
      setIsLoading(false);
    }
  }, [agencyId]);

  useEffect(() => { load(); }, [load]);
  return { properties, isLoading, reload: load };
}
